import { Link } from "react-router-dom";
import { BookOpen, Plane, Briefcase, GraduationCap, Wallet, Lightbulb, Users, Calendar, Target, Smartphone } from "lucide-react";
import SocialShareButtons from "@/components/SocialShareButtons";

const SEOIntroSection = () => {
  return (
    <section className="py-12 md:py-16 bg-muted/30">
      <div className="container">
        <div className="max-w-4xl mx-auto">
          {/* Main intro */}
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
              Hvad er MySabbat?
            </h2>
            
            <p className="text-muted-foreground leading-relaxed mb-6">
              <strong className="text-foreground">MySabbat er Danmarks uafhængige platform for unge, der overvejer eller planlægger et sabbatår.</strong> Vi giver dig overblik, inspiration og konkrete guides til alt fra rejse og backpacking til job, højskole, økonomi og fremtidsplanlægning. Vores mål er at hjælpe dig med at træffe informerede valg, så du får mest muligt ud af dit sabbatår.
            </p>

            <p className="text-muted-foreground leading-relaxed mb-8">
              Et sabbatår er en unik mulighed for at udforske verden, opdage nye interesser og samle erfaringer, før du træffer store beslutninger om uddannelse og karriere. Men det kan være svært at vide, hvor man skal starte. Derfor har vi samlet alt, hvad du behøver at vide, ét sted – helt gratis. Og snart lancerer vi <Link to="/waitlist" className="text-primary font-medium hover:underline">MySabbat-appen</Link>, så du kan planlægge dit sabbatår direkte fra mobilen.
            </p>

            {/* Who is MySabbat for */}
            <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-4 flex items-center gap-3">
              <Users className="w-6 h-6 text-primary" />
              Hvem er MySabbat for?
            </h3>
            
            <p className="text-muted-foreground leading-relaxed mb-6">
              Vores sabbatår-guides er skrevet til unge mellem 18-25 år, der står ved en skillevej i livet. Måske har du lige afsluttet gymnasiet, HF eller en erhvervsuddannelse og er usikker på, hvad det næste skridt skal være. Eller måske drømmer du om at rejse, arbejde i udlandet eller tage på højskole, men mangler overblik over dine muligheder.
            </p>

            <p className="text-muted-foreground leading-relaxed mb-8">
              Uanset om du vil backpacke gennem Sydøstasien, arbejde på en farm i Australien, tage på folkehøjskole eller bare have tid til at finde ud af, hvad du vil med dit liv – så er MySabbat for dig.
            </p>

            {/* What do you get */}
            <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-4 flex items-center gap-3">
              <Lightbulb className="w-6 h-6 text-primary" />
              Hvad får du hos MySabbat?
            </h3>
            
            <p className="text-muted-foreground leading-relaxed mb-6">
              Vi har samlet mere end 40 artikler fordelt på 9 kategorier, der dækker alle aspekter af sabbatåret – og med vores kommende app får du alt samlet ét sted på din telefon. Her er et overblik over, hvad du kan finde:
            </p>
          </div>

          {/* Category links grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <Link 
              to="/kategori/sabbataar-intro" 
              className="flex items-start gap-3 p-4 bg-card rounded-xl border border-border hover:border-primary/50 hover:shadow-md transition-all group"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                <BookOpen className="w-5 h-5 text-primary" />
              </div>
              <div>
                <span className="font-semibold text-foreground group-hover:text-primary transition-colors">Sabbatår-intro</span>
                <p className="text-sm text-muted-foreground mt-0.5">Hvad er et sabbatår, og er det noget for dig?</p>
              </div>
            </Link>

            <Link 
              to="/kategori/rejse-udland" 
              className="flex items-start gap-3 p-4 bg-card rounded-xl border border-border hover:border-primary/50 hover:shadow-md transition-all group"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                <Plane className="w-5 h-5 text-primary" />
              </div>
              <div>
                <span className="font-semibold text-foreground group-hover:text-primary transition-colors">Rejse & udland</span>
                <p className="text-sm text-muted-foreground mt-0.5">Guides til backpacking, working holiday og rejseplanlægning</p>
              </div>
            </Link>

            <Link 
              to="/kategori/arbejde-frivillighed" 
              className="flex items-start gap-3 p-4 bg-card rounded-xl border border-border hover:border-primary/50 hover:shadow-md transition-all group"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                <Briefcase className="w-5 h-5 text-primary" />
              </div>
              <div>
                <span className="font-semibold text-foreground group-hover:text-primary transition-colors">Arbejde & frivillighed</span>
                <p className="text-sm text-muted-foreground mt-0.5">Find job i udlandet eller frivilligt arbejde</p>
              </div>
            </Link>

            <Link 
              to="/kategori/hoejskole-uddannelse" 
              className="flex items-start gap-3 p-4 bg-card rounded-xl border border-border hover:border-primary/50 hover:shadow-md transition-all group"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                <GraduationCap className="w-5 h-5 text-primary" />
              </div>
              <div>
                <span className="font-semibold text-foreground group-hover:text-primary transition-colors">Højskole & uddannelse</span>
                <p className="text-sm text-muted-foreground mt-0.5">Vælg den rigtige højskole eller kursus</p>
              </div>
            </Link>

            <Link 
              to="/kategori/oekonomi-planlaegning" 
              className="flex items-start gap-3 p-4 bg-card rounded-xl border border-border hover:border-primary/50 hover:shadow-md transition-all group"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                <Wallet className="w-5 h-5 text-primary" />
              </div>
              <div>
                <span className="font-semibold text-foreground group-hover:text-primary transition-colors">Økonomi & planlægning</span>
                <p className="text-sm text-muted-foreground mt-0.5">Budget, opsparing og praktisk planlægning</p>
              </div>
            </Link>

            <Link 
              to="/kategori/kvote2-su-fremtid" 
              className="flex items-start gap-3 p-4 bg-card rounded-xl border border-border hover:border-primary/50 hover:shadow-md transition-all group"
            >
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                <Target className="w-5 h-5 text-primary" />
              </div>
              <div>
                <span className="font-semibold text-foreground group-hover:text-primary transition-colors">Kvote 2, SU & fremtid</span>
                <p className="text-sm text-muted-foreground mt-0.5">Optagelse, SU-regler og fremtidsplaner</p>
              </div>
            </Link>
          </div>

          {/* App CTA card */}
          <div className="bg-gradient-to-r from-primary to-primary/80 rounded-2xl p-6 md:p-8 mb-6">
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                <Smartphone className="w-7 h-7 text-primary-foreground" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-primary-foreground mb-1">
                  MySabbat-appen kommer snart
                </h3>
                <p className="text-primary-foreground/80">
                  Få alt samlet ét sted – guides, planlægning og inspiration direkte på din telefon. Tilmeld dig ventelisten og vær blandt de første til at prøve appen.
                </p>
              </div>
              <Link 
                to="/waitlist"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-primary font-semibold rounded-xl hover:bg-white/90 transition-colors shrink-0"
              >
                Tilmeld ventelisten
              </Link>
            </div>
          </div>

          {/* CTA to pillar article */}
          <div className="bg-card border border-border rounded-2xl p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Calendar className="w-7 h-7 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-1">
                  Ny til sabbatår?
                </h3>
                <p className="text-muted-foreground">
                  Start med vores komplette guide, der dækker alt fra planlægning til gennemførelse af dit sabbatår.
                </p>
              </div>
              <Link 
                to="/artikel/sabbataar-komplet-guide-2026"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-semibold rounded-xl hover:bg-primary/90 transition-colors shrink-0"
              >
                Læs den komplette sabbatår-guide
              </Link>
            </div>
          </div>

          {/* Social sharing */}
          <div className="mt-8 flex justify-center">
            <SocialShareButtons />
          </div>
        </div>
      </div>
    </section>
  );
};

export default SEOIntroSection;
